import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowUpRight, CheckCircle, Clock, XCircle, Loader2, Filter, RefreshCw, Globe, Megaphone, TrendingUp, PhoneCall, BrainCircuit } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const UPGRADE_META = {
  fb_ig_ads: { label: 'FB & IG Ads', icon: Megaphone, color: '#2563EB' },
  custom_website: { label: 'Custom Website', icon: Globe, color: '#059669' },
  seo_boost: { label: 'Local SEO Boost', icon: TrendingUp, color: '#b45309' },
  call_tracking: { label: 'Call Tracking', icon: PhoneCall, color: '#7c3aed' },
  call_tracking_ai: { label: 'Call Tracking + AI', icon: BrainCircuit, color: '#be185d' },
};

const STATUS_OPTIONS = [
  { value: 'requested', label: 'Requested', style: 'bg-amber-100 text-amber-800' },
  { value: 'in_progress', label: 'In Progress', style: 'bg-blue-100 text-blue-800' },
  { value: 'active', label: 'Active', style: 'bg-green-100 text-green-800' },
  { value: 'declined', label: 'Declined', style: 'bg-red-100 text-red-800' },
];

export default function AdminUpgradeRequests({ getAuthHeaders }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [updating, setUpdating] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const url = filterStatus === 'all'
        ? `${API}/api/admin/upgrade-requests`
        : `${API}/api/admin/upgrade-requests?status=${filterStatus}`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (e) {
      console.error('Failed to fetch upgrade requests:', e);
    }
    setLoading(false);
  }, [getAuthHeaders, filterStatus]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const updateStatus = async (requestId, newStatus) => {
    setUpdating(requestId);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const res = await fetch(`${API}/api/admin/upgrade-requests/${requestId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setRequests(prev => prev.map(r =>
          r.request_id === requestId ? { ...r, status: newStatus } : r
        ));
      }
    } catch (e) {
      console.error('Failed to update:', e);
    }
    setUpdating(null);
  };

  const statusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    if (!opt) return <Badge variant="outline">{status}</Badge>;
    return <Badge className={opt.style} data-testid={`admin-upgrade-status-${status}`}>{opt.label}</Badge>;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const counts = {
    all: requests.length,
    requested: requests.filter(r => r.status === 'requested').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    active: requests.filter(r => r.status === 'active').length,
    declined: requests.filter(r => r.status === 'declined').length,
  };

  return (
    <div className="space-y-6" data-testid="admin-upgrade-requests">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Upgrade Requests</h2>
          <p className="text-sm text-slate-500">Manage client upgrade requests for premium services</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRequests} data-testid="refresh-upgrades-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', count: counts.all, color: 'bg-slate-100 text-slate-700' },
          { label: 'Requested', count: counts.requested, color: 'bg-amber-50 text-amber-700' },
          { label: 'In Progress', count: counts.in_progress, color: 'bg-blue-50 text-blue-700' },
          { label: 'Active', count: counts.active, color: 'bg-green-50 text-green-700' },
          { label: 'Declined', count: counts.declined, color: 'bg-red-50 text-red-700' },
        ].map(s => (
          <div key={s.label} className={`rounded-lg p-3 text-center ${s.color}`} data-testid={`count-${s.label.toLowerCase().replace(' ', '-')}`}>
            <p className="text-2xl font-bold">{s.count}</p>
            <p className="text-xs font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-slate-400" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48" data-testid="filter-status-select">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Requests Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Loading requests...</div>
      ) : requests.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">No upgrade requests found.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="upgrade-requests-table">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <th className="px-4 py-3">Client</th>
                    <th className="px-4 py-3">Service</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Requested</th>
                    <th className="px-4 py-3">Updated</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {requests.map((req) => {
                    const meta = UPGRADE_META[req.upgrade_id] || { label: req.upgrade_id, icon: ArrowUpRight, color: '#6b7280' };
                    const Icon = meta.icon;
                    return (
                      <tr key={req.request_id} className="hover:bg-slate-50 transition-colors" data-testid={`upgrade-row-${req.request_id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800">{req.business_name || req.client_slug}</p>
                          <p className="text-xs text-slate-400">{req.client_email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded flex items-center justify-center" style={{ background: `${meta.color}15` }}>
                              <Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
                            </div>
                            <span className="font-medium text-slate-700">{meta.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">{statusBadge(req.status)}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(req.requested_at)}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(req.updated_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {updating === req.request_id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                            ) : (
                              <>
                                {req.status !== 'active' && (
                                  <Button size="sm" variant="ghost" className="h-7 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => updateStatus(req.request_id, 'active')}
                                    data-testid={`approve-${req.request_id}`}>
                                    <CheckCircle className="h-3 w-3 mr-1" /> Approve
                                  </Button>
                                )}
                                {req.status === 'requested' && (
                                  <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => updateStatus(req.request_id, 'in_progress')}
                                    data-testid={`progress-${req.request_id}`}>
                                    <Clock className="h-3 w-3 mr-1" /> In Progress
                                  </Button>
                                )}
                                {req.status !== 'declined' && (
                                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => updateStatus(req.request_id, 'declined')}
                                    data-testid={`decline-${req.request_id}`}>
                                    <XCircle className="h-3 w-3 mr-1" /> Decline
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
